import dayjs from 'dayjs';
import * as StoreReview from 'expo-store-review';
import { useState } from 'react';
import { shallow } from 'zustand/shallow';

import useGuest from 'app/hooks/useGuest';
import useStore from 'app/hooks/useStore';
import Logger from 'app/services/Logger';

const PROMPT_DELAY_SECS = 5;
const REPROMPT_TIME_WEEKS = 2;
const MODULE = '[usePromptReview]';

async function checkCanPrompt() {
  const [hasAction, isAvailable] = await Promise.all([
    StoreReview.hasAction(),
    StoreReview.isAvailableAsync(),
  ]);

  if (!hasAction) {
    Logger.info(`${MODULE} store URLs or native store review capabilities are not available`);
  }
  if (!isAvailable) {
    Logger.info(`${MODULE} platform not capable of store reviews`);
  }

  return hasAction && isAvailable;
}

export default function usePromptReview() {
  const [loading, setLoading] = useState(false);
  const guest = useGuest();
  const [nextReviewPrompt, setNextReviewPrompt] = useStore(
    (s) => [s.nextReviewPrompt, s.setNextReviewPrompt],
    shallow
  );

  const previouslyPrompted = () => {
    return !!guest?.email && !!nextReviewPrompt;
  };

  const promptForReview = async () => {
    const canPrompt = await checkCanPrompt();
    if (loading || !canPrompt || !guest.email) return;

    const currentDate = new Date();
    const nextPromptDate = nextReviewPrompt;

    if (nextPromptDate && new Date(nextPromptDate) > currentDate) return;

    setLoading(true);

    setTimeout(async () => {
      try {
        await StoreReview.requestReview();
        setNextReviewPrompt(dayjs().add(REPROMPT_TIME_WEEKS, 'weeks').toISOString());
      } catch (error) {
        Logger.error(`${MODULE} unable to request review`, { error });
        // set to now so that we can try again
        setNextReviewPrompt(dayjs().toISOString());
      } finally {
        setLoading(false);
      }
    }, PROMPT_DELAY_SECS * 1000);
  };

  return { promptForReview, previouslyPrompted };
}
